import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard, ShoppingBag, Package, FolderTree, Users, Percent, Settings, Store, Menu, X,
  FileText, Truck, RotateCcw, ShoppingCart, Star, Boxes, Factory, Tag, Layers, UserCog,
  Headphones, Mail, Image, Search, FileEdit, BarChart3, Puzzle, ShieldAlert
} from "lucide-react";
import { useState, useEffect } from "react";
import { toast } from "sonner";

const GROUPS = [
  {
    label: "Pārdošana",
    items: [
      { to: "/admin", end: true, icon: LayoutDashboard, label: "Pārskats", key: "parskats" },
      { to: "/admin/pasutijumi", icon: ShoppingBag, label: "Pasūtījumi", key: "pasutijumi" },
      { to: "/admin/rekini", icon: FileText, label: "Rēķini", key: "rekini" },
      { to: "/admin/sutijumi", icon: Truck, label: "Sūtījumi", key: "sutijumi" },
      { to: "/admin/atgriesanas", icon: RotateCcw, label: "Atgriešanas / RMA", key: "atgriesanas" },
      { to: "/admin/pamestie-grozi", icon: ShoppingCart, label: "Pamestie grozi", key: "pamestie-grozi" },
    ],
  },
  {
    label: "Katalogs",
    items: [
      { to: "/admin/produkti", icon: Package, label: "Produkti", key: "produkti" },
      { to: "/admin/kategorijas", icon: FolderTree, label: "Kategorijas", key: "kategorijas" },
      { to: "/admin/zimoli", icon: Tag, label: "Zīmoli", key: "zimoli" },
      { to: "/admin/atributi", icon: Layers, label: "Atribūti", key: "atributi" },
      { to: "/admin/krajumi", icon: Boxes, label: "Krājumi", key: "krajumi" },
      { to: "/admin/piegadataji", icon: Factory, label: "Piegādātāji", key: "piegadataji" },
      { to: "/admin/atsauksmes", icon: Star, label: "Atsauksmes", key: "atsauksmes" },
    ],
  },
  {
    label: "Klienti",
    items: [
      { to: "/admin/klienti", icon: Users, label: "Klienti", key: "klienti" },
      { to: "/admin/klientu-grupas", icon: UserCog, label: "Klientu grupas", key: "klientu-grupas" },
      { to: "/admin/atbalsts", icon: Headphones, label: "Atbalsts", key: "atbalsts" },
    ],
  },
  {
    label: "Mārketings",
    items: [
      { to: "/admin/atlaides", icon: Percent, label: "Atlaides", key: "atlaides" },
      { to: "/admin/kampanas", icon: Mail, label: "E-pasta kampaņas", key: "kampanas" },
      { to: "/admin/baneri", icon: Image, label: "Baneri", key: "baneri" },
      { to: "/admin/seo", icon: Search, label: "SEO", key: "seo" },
    ],
  },
  {
    label: "Saturs un analītika",
    items: [
      { to: "/admin/cms", icon: FileEdit, label: "CMS lapas", key: "cms" },
      { to: "/admin/atskaites", icon: BarChart3, label: "Atskaites", key: "atskaites" },
      { to: "/admin/moduli", icon: Puzzle, label: "Moduļi", key: "moduli" },
    ],
  },
  {
    label: "Konfigurācija",
    items: [
      { to: "/admin/iestatijumi", icon: Settings, label: "Iestatījumi", key: "iestatijumi" },
      { to: "/admin/lietotaji", icon: UserCog, label: "Lietotāji un lomas", key: "lietotaji" },
    ],
  },
];

const ALL_KEYS = GROUPS.flatMap((g) => g.items.map((i) => i.key));

export const ROLES = {
  superadmin: { name: "Superadmin", email: "admin@ecommercedemo.lv", desc: "Pilna piekļuve visam", allowed: ALL_KEYS },
  manager: {
    name: "Menedžeris", email: "menedzeris@ecommercedemo.lv", desc: "Viss, izņemot sistēmas iestatījumus un moduļus",
    allowed: ALL_KEYS.filter((k) => !["iestatijumi", "lietotaji", "moduli"].includes(k)),
  },
  marketing: {
    name: "Mārketings", email: "marketings@ecommercedemo.lv", desc: "Baneri, kampaņas, saturs, SEO, atlaides",
    allowed: ["parskats", "atlaides", "kampanas", "baneri", "seo", "cms", "atsauksmes", "atskaites"],
  },
  noliktava: {
    name: "Noliktava", email: "noliktava@ecommercedemo.lv", desc: "Pasūtījumi, sūtījumi, krājumi, piegādātāji",
    allowed: ["pasutijumi", "sutijumi", "atgriesanas", "produkti", "krajumi", "piegadataji"],
  },
  gramatvedis: {
    name: "Grāmatvedis", email: "gramatvediba@ecommercedemo.lv", desc: "Rēķini, pasūtījumi, klienti, atskaites (lasīšana)",
    allowed: ["parskats", "pasutijumi", "rekini", "klienti", "atskaites"],
  },
};

const keyFromPath = (pathname) => {
  const seg = pathname.replace(/^\/admin\/?/, "").split("/")[0];
  return seg === "" ? "parskats" : seg;
};

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const [role, setRole] = useState(() => localStorage.getItem("demo_admin_role") || "superadmin");
  const location = useLocation();
  useEffect(() => { localStorage.setItem("demo_admin_role", role); }, [role]);

  const perms = ROLES[role];
  const currentKey = keyFromPath(location.pathname);
  const hasAccess = perms.allowed.includes(currentKey);
  const visibleGroups = GROUPS.map((g) => ({ ...g, items: g.items.filter((i) => perms.allowed.includes(i.key)) })).filter((g) => g.items.length > 0);
  const defaultPath = GROUPS.flatMap((g) => g.items).find((i) => i.key === perms.allowed[0])?.to || "/admin";

  const changeRole = (r) => {
    setRole(r);
    toast.info(`Loma nomainīta: ${ROLES[r].name} (DEMO)`, { description: ROLES[r].desc });
  };

  const nav = (
    <nav className="flex flex-col gap-0.5 p-3 overflow-y-auto">
      {visibleGroups.map((g) => (
        <div key={g.label} className="mb-2">
          <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-white/40">{g.label}</div>
          {g.items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={`admin-nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 text-[13px] font-semibold transition-colors ${isActive ? "bg-[#D0121A] text-white" : "text-white/70 hover:bg-white/10 hover:text-white"}`}>
              <n.icon size={15} /> {n.label}
            </NavLink>
          ))}
        </div>
      ))}
      <Link to="/" data-testid="admin-back-to-shop" onClick={() => setOpen(false)}
        className="flex items-center gap-2.5 px-3 py-2 text-[13px] font-semibold text-white/70 hover:bg-white/10 hover:text-white transition-colors border-t border-white/10 mt-2 pt-3">
        <Store size={15} /> Uz veikalu
      </Link>
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F5F5F5] flex">
      <aside className="hidden lg:flex flex-col w-60 bg-[#111111] shrink-0 max-h-screen sticky top-0">
        <div className="p-4 border-b border-white/10 shrink-0">
          <div className="text-lg font-extrabold text-white leading-none">E-Commerce <span className="text-[#FF3B44]">DEMO</span></div>
          <div className="text-[10px] uppercase tracking-widest text-white/50 mt-1">Admin panelis</div>
        </div>
        {nav}
      </aside>
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-[#E2E2E2] px-4 py-2.5 flex items-center gap-3 sticky top-0 z-40 flex-wrap">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="admin-mobile-menu" aria-label="Izvēlne"><Menu size={22} /></button>
          <span className="text-sm font-bold uppercase tracking-widest hidden md:block">Administrēšana (DEMO)</span>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-2 text-xs font-bold">
              <span className="text-[#6B6B6B] uppercase tracking-wider hidden sm:block">Loma:</span>
              <select value={role} onChange={(e) => changeRole(e.target.value)} data-testid="admin-role-select"
                className="text-xs font-bold border border-[#E2E2E2] px-2.5 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-[#111111] cursor-pointer">
                {Object.entries(ROLES).map(([k, r]) => <option key={k} value={k}>{r.name}</option>)}
              </select>
            </label>
            <span className="text-xs bg-[#F5F5F5] px-3 py-2 font-semibold hidden xl:block" data-testid="admin-role-email">{perms.email}</span>
            <Link to="/" data-testid="admin-header-to-shop"
              className="flex items-center gap-1.5 bg-[#D0121A] hover:bg-[#A80D14] text-white text-xs font-bold px-4 py-2 transition-colors">
              <Store size={14} /> Uz veikalu
            </Link>
          </div>
        </header>
        <main className="p-4 lg:p-6">
          {hasAccess ? <Outlet /> : (
            <div className="max-w-lg mx-auto text-center py-16" data-testid="admin-access-denied">
              <ShieldAlert size={56} className="mx-auto text-[#D0121A]" />
              <h1 className="text-2xl font-extrabold uppercase mt-4">Nav piekļuves</h1>
              <p className="text-sm text-[#6B6B6B] mt-2">Lomai <span className="font-bold text-[#111111]">{perms.name}</span> šī sadaļa nav pieejama.<br />{perms.desc}.</p>
              <div className="flex gap-3 justify-center mt-6">
                <Link to={defaultPath} data-testid="access-denied-home" className="bg-[#111111] hover:bg-[#3A3A3A] text-white text-sm font-bold px-6 py-3 transition-colors">Uz pieejamo sadaļu</Link>
                <button onClick={() => changeRole("superadmin")} data-testid="access-denied-switch" className="border-2 border-[#E2E2E2] hover:border-[#D0121A] hover:text-[#D0121A] text-sm font-bold px-6 py-3 transition-colors">Pārslēgties uz Superadmin</button>
              </div>
            </div>
          )}
        </main>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-[#111111] overflow-y-auto">
            <div className="p-4 flex justify-between items-center border-b border-white/10">
              <span className="text-white font-extrabold">Admin</span>
              <button onClick={() => setOpen(false)} aria-label="Aizvērt"><X size={20} className="text-white" /></button>
            </div>
            {nav}
          </div>
        </div>
      )}
    </div>
  );
}
